"use client";

import React, { useCallback, useMemo } from "react";
import { Copy, Filter } from "lucide-react";
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/context-menu";
import { useTableRowActionContext } from "@/lib/hooks/ui/useTableRowActionContext";
import {
  executeDetailAction,
} from "@/components/shared/UniversalDetailPage/actions/execute-action";
import {
  MORE_ACTIONS_LABEL,
  partitionActionGroups,
  type ActionCategory,
} from "@/lib/actions/action-categories";
import type {
  DetailAction,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type {
  EntityHooks,
  SimpleTableConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";
import type { RowActionRoleFilter } from "@/components/shared/UniversalCRMTable/contexts/SimpleTableRowServicesContext";

/**
 * The right-click menu body, with the update/delete mutations and the role
 * filter INJECTED.
 *
 * Split out of `RowContextMenuContent` (which keeps subscribing per instance
 * for standalone surfaces) so rows inside a `SimpleTable` can share the ONE
 * table-level subscription from `SimpleTableRowServicesContext`. This
 * component renders once PER ROW, so its two mutation hooks were ~70 React
 * Query subscriptions per rendered table on their own, and its
 * `useActionFiltering()` call was one more `useRoleCheck` +
 * `useSequencesAccess` pair per row.
 */

/**
 * The cell the user right-clicked, captured by TableRow from the
 * data-field-* attributes on TableCellWrapper. Drives the cell-specific
 * section (copy / filter-by-value) at the top of the row menu.
 */
export interface ActiveCell {
  fieldId: string;
  label: string;
  /** Stringified primitive value, or undefined for non-filterable cells. */
  filterValue?: string;
  /** Trimmed rendered text of the cell, or undefined when empty. */
  copyText?: string;
}

export interface RowContextMenuContentViewProps<T extends BaseEntity> {
  item: T;
  actions: DetailAction<T>[];
  hooks: EntityHooks<T>;
  entityName: string;
  config?: SimpleTableConfig<T>;
  /** Right-clicked cell (cell-aware menu); null when not over a cell. */
  activeCell?: ActiveCell | null;
  /** Copy the cell's rendered text. Provided by TableRow. */
  onCopyCellValue?: (text: string) => void;
  /** Seed a simple filter from the cell value. Omitted when filtering is off. */
  onFilterByCellValue?: (fieldId: string, value: string) => void;
  /** Injected by the caller — see the note above on where they come from. */
  updateMutation: ReturnType<EntityHooks<T>["useUpdate"]>;
  deleteMutation: ReturnType<EntityHooks<T>["useDelete"]>;
  filterActionsByRole: RowActionRoleFilter;
}

export function RowContextMenuContentView<T extends BaseEntity>({
  item,
  actions,
  hooks,
  entityName,
  config,
  activeCell,
  onCopyCellValue,
  onFilterByCellValue,
  updateMutation,
  deleteMutation,
  filterActionsByRole,
}: RowContextMenuContentViewProps<T>) {
  const refetch = useCallback(() => Promise.resolve(), []);

  const rowActionContext = useTableRowActionContext({
    item,
    hooks,
    refetch,
    updateMutation,
    deleteMutation,
    entityName,
    config,
  });

  const visibleActions = useMemo(() => {
    const roleFilteredActions = filterActionsByRole(actions);
    return roleFilteredActions.filter((action) => {
      if (action.visible && typeof action.visible === "function") {
        return action.visible(item);
      }
      return true;
    });
  }, [actions, item, filterActionsByRole]);

  // Order by priority, then split into render zones: top (inline), more
  // (collapsed behind a "More…" submenu), danger (pinned last, always inline).
  const { top, more, danger } = useMemo(
    () => partitionActionGroups(visibleActions),
    [visibleActions]
  );

  const handleAction = useCallback(
    async (action: DetailAction<T>) => {
      if (action.disabled && typeof action.disabled === "function" && action.disabled(item)) {
        return;
      }
      if (!rowActionContext) return;

      await executeDetailAction(action, rowActionContext);
    },
    [item, rowActionContext]
  );

  const renderItem = useCallback(
    (action: DetailAction<T>) => {
      const label =
        typeof action.label === "function" ? action.label(item) : action.label;
      const isDisabled =
        action.disabled &&
        typeof action.disabled === "function" &&
        action.disabled(item);
      return (
        <ContextMenuItem
          key={action.id}
          disabled={isDisabled}
          variant={action.variant === "destructive" ? "destructive" : "default"}
          onSelect={() => handleAction(action)}
        >
          {label}
        </ContextMenuItem>
      );
    },
    [item, handleAction]
  );

  // A non-interactive section header (styled label, NOT a selectable item) so
  // categories read as distinct groups. The list is kept short by the
  // partition — the integration/admin tail folds behind "More actions".
  const renderGroup = useCallback(
    (group: { category: ActionCategory; label: string; items: DetailAction<T>[] }) => (
      <React.Fragment key={group.category}>
        <ContextMenuLabel className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {group.label}
        </ContextMenuLabel>
        {group.items.map(renderItem)}
      </React.Fragment>
    ),
    [renderItem]
  );

  // Cell-specific actions (copy / filter-by-value) for the right-clicked cell.
  const copyText = activeCell?.copyText;
  const filterFieldId = activeCell?.fieldId;
  const filterValue = activeCell?.filterValue;
  const canCopyCell = Boolean(copyText && onCopyCellValue);
  const canFilterCell = Boolean(
    onFilterByCellValue && filterFieldId && filterValue !== undefined
  );
  const hasCellSection = canCopyCell || canFilterCell;

  if (!rowActionContext || (visibleActions.length === 0 && !hasCellSection)) {
    return null;
  }

  // Whether anything renders before the "More…" zone (controls its divider).
  const needsSeparatorBeforeMore = top.length > 0;

  return (
    <ContextMenuContent className="w-52">
      {hasCellSection && (
        <>
          {canCopyCell && (
            <ContextMenuItem
              onSelect={() => {
                if (copyText && onCopyCellValue) onCopyCellValue(copyText);
              }}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy value
            </ContextMenuItem>
          )}
          {canFilterCell && (
            <ContextMenuItem
              onSelect={() => {
                if (onFilterByCellValue && filterFieldId && filterValue !== undefined) {
                  onFilterByCellValue(filterFieldId, filterValue);
                }
              }}
            >
              <Filter className="mr-2 h-4 w-4" />
              Filter by &ldquo;{activeCell?.label}&rdquo;
            </ContextMenuItem>
          )}
          {visibleActions.length > 0 && <ContextMenuSeparator />}
        </>
      )}

      {top.map((group, groupIndex) => (
        <React.Fragment key={group.category}>
          {groupIndex > 0 && <ContextMenuSeparator />}
          {renderGroup(group)}
        </React.Fragment>
      ))}

      {more.length > 0 && (
        <>
          {needsSeparatorBeforeMore && <ContextMenuSeparator />}
          <ContextMenuSub>
            <ContextMenuSubTrigger>{MORE_ACTIONS_LABEL}</ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-52">
              {more.map((group, groupIndex) => (
                <React.Fragment key={group.category}>
                  {groupIndex > 0 && <ContextMenuSeparator />}
                  {renderGroup(group)}
                </React.Fragment>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        </>
      )}

      {danger.flatMap((group) => group.items).length > 0 && (
        <>
          {(top.length > 0 || more.length > 0) && <ContextMenuSeparator />}
          {danger.map((group) => group.items.map(renderItem))}
        </>
      )}
    </ContextMenuContent>
  );
}
